import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Typography, InputLabel, ToggleButtonGroup } from '@mui/material';
import {
  FlexColumn,
  FileUploadInput,
  ToggleButton,
  UrlInput,
  Button,
  Flex,
} from '@elementary/common-ui/components';
import { Controller, UseFormReturn } from 'react-hook-form';
import { SimpleFile } from '@elementary/common-ui/components/FileUploadInput/FileUploadInput';
import {
  ErrorMessage,
  InputWrapper,
} from '@elementary/common-ui/components/FormControls';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import {
  fontFamily,
  fontSize,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import { DomainFormItem } from '../types';
import DomainListItem from './DomainListItem';

const Section = styled(FlexColumn)`
  gap: ${space('sm')};
`;

const SectionLabel = styled(InputLabel)`
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  font-size: ${fontSize('base')};
  color: ${({ theme }) => theme.palette.content.primary};
`;

const HelperText = styled(Typography)`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.tertiary};
`;

const XmlPreview = styled.pre<{ $hasError?: boolean }>`
  background-color: ${({ theme }) => theme.palette.surface.sunken};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError
        ? theme.palette.status.error.main
        : theme.palette.border.default};
  border-radius: ${radius('md')};
  padding: ${space('md')};
  font-size: ${fontSize('sm')};
  font-family: ${fontFamily('monoSystem')};
  overflow-x: auto;
  max-height: 120px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
`;

const DomainsList = styled(FlexColumn)`
  gap: ${space('xl')};
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  width: 100%;

  .MuiToggleButton-root {
    flex: 1;
    border-radius: ${radius('md')};
  }
`;

export type SsoConfigFormData = {
  metadataInputMethod: 'file' | 'url';
  fileName: string;
  url: string;
  metadataXml: string | null;
  domains: DomainFormItem[];
};

type Props = {
  methods: UseFormReturn<SsoConfigFormData>;
  domainValidationErrors: Array<string | null>;
  onDomainBlur: () => void;
  xmlError: string | null;
};

const SsoConfigForm = ({
  methods,
  domainValidationErrors,
  onDomainBlur,
  xmlError,
}: Props) => {
  const [fetchError, setFetchError] = useState<string | null>(null);

  const handleAddDomain = useCallback(() => {
    const currentDomains = methods.getValues('domains');
    const newDomains = [...currentDomains, { domain: '', status: 'unknown' }];
    methods.setValue('domains', newDomains as DomainFormItem[]);
  }, [methods]);

  const handleDomainChange = useCallback(
    (index: number, value: string) => {
      const currentDomains = methods.getValues('domains');
      const newDomains = currentDomains.map((d, i) =>
        i === index ? { ...d, domain: value, status: 'unknown' } : d,
      );
      methods.setValue('domains', newDomains as DomainFormItem[]);
    },
    [methods],
  );

  const handleRemoveDomain = useCallback(
    (index: number) => {
      const currentDomains = methods.getValues('domains');
      const newDomains = currentDomains.filter((_, i) => i !== index);
      methods.setValue('domains', newDomains);
    },
    [methods],
  );

  const metadataInputMethod = methods.watch('metadataInputMethod');
  const fileName = methods.watch('fileName');

  return (
    <>
      <Section>
        <SectionLabel>SAML Metadata</SectionLabel>
        <StyledToggleButtonGroup
          value={metadataInputMethod}
          exclusive
          onChange={(_, value) =>
            value && methods.setValue('metadataInputMethod', value)
          }
        >
          <ToggleButton value="file">Upload file</ToggleButton>
          <ToggleButton value="url">URL</ToggleButton>
        </StyledToggleButtonGroup>

        {metadataInputMethod === 'file' ? (
          <>
            <HelperText>
              Upload your identity provider&apos;s metadata XML file
            </HelperText>
            <FileUploadInput
              value={{
                name: methods.watch('fileName'),
                content: methods.watch('metadataXml') ?? '',
              }}
              onChange={(file) => {
                methods.setValue('fileName', file?.name ?? '');
                methods.setValue(
                  'metadataXml',
                  file ? (file as SimpleFile)?.content : null,
                );
                setFetchError(null);
              }}
              placeholder="Drop XML file here or click to browse"
            />
            {fileName && <HelperText>Selected File: {fileName}</HelperText>}
          </>
        ) : (
          <>
            <HelperText>
              Paste your identity provider&apos;s metadata URL
            </HelperText>
            <Controller
              control={methods.control}
              name="url"
              render={({ field }) => (
                <InputWrapper>
                  <UrlInput
                    size="large"
                    placeholder="https://your-idp.com/metadata.xml"
                    {...field}
                    onResponse={({ code, text }) => {
                      if (code === 200) {
                        methods.setValue('metadataXml', text);
                        setFetchError(null);
                      } else {
                        setFetchError('Error fetching metadata');
                      }
                    }}
                    onFetchError={(error) => {
                      setFetchError(error.message);
                      methods.setValue('metadataXml', null);
                    }}
                  />
                  <ErrorMessage>{fetchError}</ErrorMessage>
                </InputWrapper>
              )}
            />
          </>
        )}
        {methods.watch('metadataXml') && (
          <XmlPreview $hasError={!!xmlError}>
            {methods.watch('metadataXml')}
          </XmlPreview>
        )}
        {xmlError && (
          <InputWrapper>
            <ErrorMessage>{xmlError}</ErrorMessage>
          </InputWrapper>
        )}
      </Section>

      <Section>
        <SectionLabel>Domains</SectionLabel>
        <HelperText>
          Users with these email domains will sign in via SSO
        </HelperText>
        <Controller
          control={methods.control}
          name="domains"
          render={({ field }) => (
            <>
              <DomainsList>
                {field.value.map((domain, index) => (
                  <InputWrapper key={index}>
                    <DomainListItem
                      domain={domain.domain}
                      status={domain.status}
                      onDomainChange={(value) =>
                        handleDomainChange(index, value)
                      }
                      onRemove={() => handleRemoveDomain(index)}
                      onBlur={onDomainBlur}
                    />
                    <ErrorMessage>{domainValidationErrors[index]}</ErrorMessage>
                  </InputWrapper>
                ))}
              </DomainsList>
            </>
          )}
        />
        <Flex sx={{ paddingTop: 1 }}>
          <Button
            color="secondary"
            startIcon={<PlusIcon />}
            onClick={handleAddDomain}
          >
            Add domain
          </Button>
        </Flex>
      </Section>
    </>
  );
};

export default SsoConfigForm;
